import { TenantType } from '@xeetr/types/enums'
import {
  Bell,
  BookMarked,
  Briefcase,
  Building2,
  Calendar,
  Clock,
  DoorOpen,
  FlaskConicalIcon,
  FolderTree,
  IdCardLanyardIcon,
  Key,
  LayoutDashboard,
  Mail,
  Palette,
  Server,
  User,
  Utensils,
} from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { useTenant } from '../useTenant'

export interface SettingsMenuItem {
  label: string
  icon: React.ElementType
  route: string
  command: () => void
  visible?: boolean
}

export const useSettingsTabs = (): SettingsMenuItem[] => {
  const navigate = useNavigate()
  const { tenant } = useTenant()

  const navigateToUser = useCallback(() => navigate('user'), [navigate])
  const navigateToTenant = useCallback(() => navigate('tenant'), [navigate])
  const navigateToRestaurant = useCallback(() => navigate('restaurant'), [navigate])
  const navigateToService = useCallback(() => navigate('service'), [navigate])
  const navigateToDashboard = useCallback(() => navigate('dashboard-settings'), [navigate])
  const navigateToCalendar = useCallback(() => navigate('calendar-settings'), [navigate])
  const navigateToTheme = useCallback(() => navigate('theme'), [navigate])
  const navigateToNotifications = useCallback(() => navigate('notifications'), [navigate])
  const navigateToEmails = useCallback(() => navigate('emails'), [navigate])
  const navigateToShifts = useCallback(() => navigate('shifts'), [navigate])
  const navigateToRooms = useCallback(() => navigate('rooms'), [navigate])
  const navigateToEmployees = useCallback(() => navigate('employees'), [navigate])
  const navigateToServices = useCallback(() => navigate('services'), [navigate])
  const navigateToCategories = useCallback(() => navigate('categories'), [navigate])
  const navigateToExternalApi = useCallback(() => navigate('external-api'), [navigate])
  const navigateToMailServer = useCallback(() => navigate('mail-server'), [navigate])

  return useMemo(
    () => [
      {
        label: 'Benutzer',
        icon: User,
        command: navigateToUser,
        route: 'user',
      },
      {
        label: 'Unternehmen',
        icon: Building2,
        command: navigateToTenant,
        route: 'tenant',
      },
      {
        label: 'Reservierungen',
        icon: Utensils,
        visible: tenant?.type === TenantType.FOOD,
        command: navigateToRestaurant,
        route: 'restaurant',
      },
      {
        label: 'Buchungen',
        icon: BookMarked,
        visible: tenant?.type === TenantType.SERVICE,
        command: navigateToService,
        route: 'service',
      },
      {
        label: 'Schichten',
        icon: Clock,
        visible: tenant?.type === TenantType.FOOD,
        command: navigateToShifts,
        route: 'shifts',
      },
      {
        label: 'Räume',
        icon: DoorOpen,
        visible: tenant?.type === TenantType.FOOD,
        command: navigateToRooms,
        route: 'rooms',
      },
      {
        label: 'Mitarbeiter',
        icon: IdCardLanyardIcon,
        visible: tenant?.type === TenantType.SERVICE,
        command: navigateToEmployees,
        route: 'employees',
      },
      {
        label: 'Dienstleistungen',
        icon: Briefcase,
        visible: tenant?.type === TenantType.SERVICE,
        command: navigateToServices,
        route: 'services',
      },
      {
        label: 'Kategorien',
        icon: FolderTree,
        visible: tenant?.type === TenantType.SERVICE,
        command: navigateToCategories,
        route: 'categories',
      },
      {
        label: 'Dashboard-Anzeige',
        icon: LayoutDashboard,
        command: navigateToDashboard,
        route: 'dashboard-settings',
        visible: true,
      },
      {
        label: 'Kalenderansicht',
        icon: Calendar,
        command: navigateToCalendar,
        route: 'calendar-settings',
        visible: true,
      },
      {
        label: 'Benachrichtigungen',
        icon: Bell,
        command: navigateToNotifications,
        route: 'notifications',
      },
      {
        label: 'Design',
        icon: Palette,
        command: navigateToTheme,
        route: 'theme',
      },
      {
        label: 'E-Mails',
        icon: Mail,
        command: navigateToEmails,
        route: 'emails',
      },
      {
        label: 'Mail-Server',
        icon: Server,
        command: navigateToMailServer,
        route: 'mail-server',
        visible: true,
      },
      {
        label: 'Features',
        icon: FlaskConicalIcon,
        command: () => navigate('features'),
        route: 'features',
        visible: true,
      },
      {
        label: 'Externe API',
        icon: Key,
        command: navigateToExternalApi,
        route: 'external-api',
        visible: true,
      },
    ],
    [
      tenant?.type,
      navigateToUser,
      navigateToTenant,
      navigateToRestaurant,
      navigateToService,
      navigateToDashboard,
      navigateToCalendar,
      navigateToNotifications,
      navigateToEmails,
      navigateToTheme,
      navigateToShifts,
      navigateToRooms,
      navigateToEmployees,
      navigateToServices,
      navigateToCategories,
      navigateToExternalApi,
      navigateToMailServer,
    ]
  )
}
